import React, { useState, useEffect } from "react";

import MetadataPage from "./pages/MetadataPage";
import PriorArtPage from "./pages/PriorArtPage";
import ViewDownloadPage from "./pages/ViewDownloadPage";
import ThankYouPage from "./pages/ThankYouPage";
import WelcomePage from "./pages/WelcomePage";
import LandingPage from "./pages/LandingPage";
import LoginPage from "./pages/LoginPage";
import Footer from "./components/Footer";
import PrivacyPolicy from "./pages/PrivacyPolicy";
import TermsOfUse from "./pages/TermsOfUse";
import Disclaimer from "./pages/Disclaimer";

// App-level styles are intentionally empty — styles live inside each page component

function App() {
  // Steps: 0 = Landing, 1 = Login, 2 = Welcome, 3 = Metadata, 4 = PriorArt, 5 = View/Download
  const [step, setStep] = useState(() => {
    try {
      const s = parseInt(localStorage.getItem("appStep"), 10);
      return Number.isFinite(s) ? s : 0;
    } catch (e) {
      return 0;
    }
  });
  const [showLogoutConfirm, setShowLogoutConfirm] = useState(false);
  const [route, setRoute] = useState(null);
  const [pendingNavigation, setPendingNavigation] = useState(null);
  const [formData, setFormData] = useState({
    title: "",
    introduction: "",
    internalProjectId: "",
    createdBy: "",
    features: [{ id: Date.now(), text: "" }],
    keywords: [{ id: Date.now() + 1, text: "" }],
    patents: [],
  });
  const updateData = (field, value) =>
    setFormData((prev) => ({ ...prev, [field]: value }));
  const nextStep = () => setStep((s) => s + 1);
  const prevStep = () => setStep((s) => s - 1);

  const startNew = () => {
    setFormData({
      title: "",
      introduction: "",
      internalProjectId: "",
      createdBy: "",
      features: [{ id: Date.now(), text: "" }],
      keywords: [{ id: Date.now() + 1, text: "" }],
      patents: [],
    });
    setStep(3);
  };

  const goToThankYou = () => setStep(6);

  useEffect(() => {
    try {
      localStorage.setItem("appStep", String(step));
    } catch (e) {}
  }, [step]);

  // Ensure new pages render from the top whenever the route or step changes
  useEffect(() => {
    try {
      if (typeof window !== "undefined") {
        // primary document scroll reset
        try {
          window.scrollTo({ top: 0, left: 0, behavior: "auto" });
        } catch (e) {
          window.scrollTo(0, 0);
        }
        try {
          if (document && document.documentElement)
            document.documentElement.scrollTop = 0;
          if (document && document.body) document.body.scrollTop = 0;
        } catch (e) {}

        // reset common inner scroll containers after render (small timeout)
        setTimeout(() => {
          try {
            const resetters = document.querySelectorAll(
              ".table-wrapper, .page-wrap, .scrollable"
            );
            resetters.forEach((el) => {
              try {
                el.scrollTop = 0;
              } catch (e) {}
            });
          } catch (e) {}
        }, 40);
      }
    } catch (e) {
      // ignore
    }
  }, [step, route]);

  const onLogoutRequest = (target) => {
    // target can be 'login', 'about', or null for generic logout
    setPendingNavigation(target || null);
    setShowLogoutConfirm(true);
  };
  const confirmLogout = async () => {
    setShowLogoutConfirm(false);
    try {
      // perform sign out if auth available
      const { auth } = await import("./services/firebase");
      if (auth && typeof auth.signOut === "function") {
        await auth.signOut();
      }
    } catch (e) {
      console.warn("Logout error:", e);
    }
    try {
      localStorage.clear();
      sessionStorage.clear();
    } catch (e) {}
    // navigate after sign-out according to pendingNavigation
    if (pendingNavigation === "about") {
      setStep(0);
    } else if (pendingNavigation === "login") {
      setStep(1);
    } else {
      setStep(1);
    }
    setPendingNavigation(null);
  };
  const cancelLogout = () => setShowLogoutConfirm(false);

  const showFooter = typeof window !== "undefined" && step !== 1;

  const handleNavigate = (target) => {
    // internal app routing from Footer
    if (!target) return;
    if (target === "privacy") return setRoute("privacy");
    if (target === "terms") return setRoute("terms");
    if (target === "disclaimer") return setRoute("disclaimer");
    if (target === "login") return setStep(1);
    if (target === "about") return setStep(0);
    // unknown -> clear
    return setRoute(null);
  };

  return (
    <div className="app-shell">
      <main className="main-content">
        {route === "privacy" ? (
          <PrivacyPolicy />
        ) : route === "terms" ? (
          <TermsOfUse />
        ) : route === "disclaimer" ? (
          <Disclaimer />
        ) : (
          <>
            {step === 0 && (
              <LandingPage
                onGetStarted={() => setStep(1)}
                onSignIn={() => setStep(1)}
              />
            )}
            {step === 1 && (
              <LoginPage
                onLoginSuccess={() => setStep(2)}
                onBack={() => setStep(0)}
              />
            )}
            {step === 2 && (
              <WelcomePage createNew={startNew} onLogout={onLogoutRequest} />
            )}
            {step === 3 && (
              <MetadataPage
                data={formData}
                updateData={updateData}
                next={nextStep}
                back={prevStep}
                onLogout={onLogoutRequest}
              />
            )}
            {step === 4 && (
              <PriorArtPage
                data={formData}
                updateData={updateData}
                next={nextStep}
                back={prevStep}
                onLogout={onLogoutRequest}
              />
            )}
            {step === 5 && (
              <ViewDownloadPage
                data={formData}
                back={prevStep}
                onLogout={onLogoutRequest}
                onFinish={goToThankYou}
              />
            )}
            {step === 6 && (
              <ThankYouPage
                backToDashboard={() => setStep(2)}
                startNew={startNew}
                onLogout={onLogoutRequest}
                data={formData}
              />
            )}
          </>
        )}

        {showLogoutConfirm && (
          <div
            style={{
              position: "fixed",
              inset: 0,
              background: "rgba(0,0,0,0.4)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              zIndex: 9999,
            }}
          >
            <div
              style={{
                background: "white",
                padding: 20,
                borderRadius: 8,
                width: 360,
                boxShadow: "0 8px 32px rgba(0,0,0,0.2)",
              }}
            >
              <h3 style={{ marginTop: 0 }}>Confirm Logout</h3>
              <p style={{ color: "#374151" }}>
                Are you sure you want to sign out?
              </p>
              <div
                style={{
                  display: "flex",
                  justifyContent: "flex-end",
                  gap: 8,
                  marginTop: 12,
                }}
              >
                <button
                  onClick={cancelLogout}
                  style={{
                    padding: "8px 12px",
                    borderRadius: 6,
                    border: "1px solid #e5e7eb",
                    background: "white",
                  }}
                >
                  Cancel
                </button>
                <button
                  onClick={confirmLogout}
                  style={{
                    padding: "8px 12px",
                    borderRadius: 6,
                    border: "none",
                    background: "#ef4444",
                    color: "white",
                  }}
                >
                  Sign out
                </button>
              </div>
            </div>
          </div>
        )}
      </main>
      {showFooter && <Footer navigate={handleNavigate} />}
    </div>
  );
}

export default App;
